import {
  DelimitedContent,
  HazardDetails,
  Icon,
  Slash,
} from '@fv/client-components'
import {
  formatNumber,
  getDistanceDisplay,
  getEquipmentLabel,
} from '@fv/client-core'
import type { LoadEquipment } from '@fv/client-types'
import type { Locale } from '@fv/models'

import { Accessorials } from '@/components/shared/EquipmentAccessorial'
import { useItemDetailParts } from '@/hooks/useItemDetailParts'
import { useLocale } from '@/hooks/useLocale'
import { useTypeNames } from '@/hooks/useTypeNames'
import type { Opportunity } from '@/types/Opportunity'

type LoadDetailsProps = {
  load: Opportunity
}

export const LoadDetails = ({ load }: LoadDetailsProps) => {
  const {
    documents,
    equipmentRequested,
    items,
    quotedEquipmentType,
    status,
    workflow,
  } = load
  const { equipmentName } = useTypeNames()
  const locale = useLocale()
  const isAwarded = status === 'awarded' || status === 'confirmed'
  const getItemDetailParts = useItemDetailParts()
  const equipmentLabel = getEquipmentLabel({
    altEquipmentNames: equipmentRequested.alternateTypes?.map(equipmentName),
    equipmentName: equipmentName(
      quotedEquipmentType ?? equipmentRequested.type,
    ),
    isAwarded: isAwarded || !!quotedEquipmentType,
  })
  const otherDocs =
    documents?.filter(d => d.type === 'other' && d.source === 'shipper') ?? []

  const showShipperDocs =
    status !== 'confirmed' && status !== 'lost' && otherDocs.length > 0

  return (
    <>
      <li className="general-list__item items-start">
        <Icon icon="truck" className="color-dark" transform="down-2.5" />
        <p className="ml-2 pb-0">
          {!!load.distance && `${getDistanceDisplay(load, locale)} / `}
          Max weight of {formatNumber(equipmentRequested.weight, locale)} total{' '}
          {equipmentRequested.weightUOM}.
          {equipmentRequested.declaredValue &&
            ` valued at ${composeDeclaredValue(
              equipmentRequested,
              locale,
            )}`}{' '}
          via {equipmentLabel}
          <Accessorials
            accessorials={equipmentRequested.accessorials}
            isHazardous={equipmentRequested.isHazardous}
          />
          {load.equipmentRequested.description &&
            ` / ${load.equipmentRequested.description}`}
        </p>
      </li>

      {items?.map((item, index) => (
        <li className="general-list__item flex items-start" key={index}>
          <Icon icon="dot-circle" className="color-dark" transform="down-3" />

          <p className="mb-0 ml-2">
            <DelimitedContent delimiter={<Slash />}>
              {getItemDetailParts(item, workflow)}
            </DelimitedContent>
            {item?.hazardous?.length ? (
              <>
                {' / '}
                <Icon icon="radiation-alt" className="color-secondary" />
                <span className="ml-1">
                  Hazardous
                  <HazardDetails
                    hazardDetails={item?.hazardous}
                    description={item?.description}
                  />
                </span>
              </>
            ) : (
              item?.description && ` / ${item?.description}`
            )}
          </p>
        </li>
      ))}
      {showShipperDocs && (
        <li className="general-list__item items-start whitespace-pre-wrap">
          <Icon icon="file-alt" className="color-dark" transform="down-2.5" />
          <span className="mr-2">Shipper files attached:</span>
          {otherDocs?.map((document, index) => [
            index > 0 && ' / ',
            <a
              className="standard-link--secondary"
              href={document.url}
              key={document._id}
              rel="noreferrer"
              target="_blank"
            >
              File {index + 1}
            </a>,
          ])}
        </li>
      )}
    </>
  )
}

function composeDeclaredValue(equipment: LoadEquipment, locale: Locale) {
  const { declaredValue, declaredValueCurrency } = equipment

  if (!declaredValue) return ''

  return `$${formatNumber(
    declaredValue,
    locale,
  )} ${declaredValueCurrency.toUpperCase()}`
}
